import { useEffect, useRef, useState } from 'react';
import { ChevronsRight } from 'lucide-react';
import type { TrainingProgressView } from '../../../hooks/useTrainingProgress';
import { ProgressCell } from './ProgressCell';

/**
 * The people x modules grid. The person column sticks while module
 * columns scroll horizontally; an empty cell means that module isn't
 * assigned to that person.
 *
 * Module titles wrap inside a bounded column so a handful of long titles
 * doesn't push the table wider than the page. When it still overflows (many
 * modules), the fade + hint on the right edge makes that obvious, since
 * overlay scrollbars on macOS are invisible until you scroll.
 */
export function ProgressMatrix({
  view,
  onOpenCell,
}: {
  view: TrainingProgressView;
  onOpenCell: (personId: string, taskId: string, moduleId: string) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const [moreRight, setMoreRight] = useState(false);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const update = () =>
      setMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [view]);

  return (
    <div className="relative">
      <div ref={scroller} className="overflow-x-auto rounded-token border border-line/10">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-surface-2 text-xs uppercase tracking-wide text-fg-muted">
            <tr className="align-bottom">
              <th className="sticky left-0 z-10 min-w-[10rem] border-r border-line/10 bg-surface-2 px-3 py-2 text-left">
                Person
              </th>
              <th className="px-3 py-2 text-left font-medium">Done</th>
              {view.modules.map((m) => (
                <th
                  key={m.id}
                  className="min-w-[7rem] max-w-[11rem] px-3 py-2 text-left font-medium leading-tight"
                >
                  {m.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-divider/10 bg-surface">
            {view.people.map((row) => (
              <tr key={row.person.id}>
                <td className="sticky left-0 z-10 max-w-[14rem] border-r border-line/10 bg-surface px-3 py-2">
                  <div className="truncate font-medium text-fg">{row.person.full_name}</div>
                  <div className="truncate text-xs text-fg-dim">
                    {row.person.position ?? row.person.email}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <span
                    className={`inline-flex rounded-token-sm px-2 py-0.5 text-xs font-semibold tabular-nums ${
                      row.summary.passed === row.summary.assigned
                        ? 'bg-success-soft/15 text-success-soft-fg'
                        : 'bg-warning-soft/15 text-warning-soft-fg'
                    }`}
                    title={`${row.summary.passed} of ${row.summary.assigned} assigned modules completed`}
                  >
                    {row.summary.passed} / {row.summary.assigned}
                  </span>
                </td>
                {view.modules.map((m) => {
                  const cell = row.cells[m.id];
                  return (
                    <td key={m.id} className="px-3 py-2">
                      {cell ? (
                        <ProgressCell
                          cell={cell}
                          graded={m.graded}
                          onOpen={() => onOpenCell(row.person.id, cell.task_id, m.id)}
                        />
                      ) : (
                        <span className="text-fg-dim">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {moreRight ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 flex w-16 items-start justify-end rounded-r-token bg-gradient-to-l from-bg to-transparent pr-2 pt-2"
        >
          <span className="inline-flex items-center gap-0.5 rounded-token-sm bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-fg-muted shadow-token-sm">
            scroll <ChevronsRight className="h-3 w-3" />
          </span>
        </div>
      ) : null}
    </div>
  );
}
